import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "content"]
  static values = {
    allowHtml: { type: Boolean, default: false },
    placement: { type: String, default: "right" },
    interactive: { type: Boolean, default: false }
  }

  connect() {
    this.triggerElement = this.hasTriggerTarget ? this.triggerTarget : this.element
    this.handleEnter = this.show.bind(this)
    this.handleLeave = this.hide.bind(this)
    this.handleFocus = this.show.bind(this)
    this.handleBlur = this.hide.bind(this)
    this.handleEscape = this.onEscape.bind(this)
    this.hideTimeout = null

    this.triggerElement.addEventListener("mouseenter", this.handleEnter)
    this.triggerElement.addEventListener("mouseleave", this.handleLeave)
    this.triggerElement.addEventListener("focus", this.handleFocus)
    this.triggerElement.addEventListener("blur", this.handleBlur)
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.triggerElement?.removeEventListener("mouseenter", this.handleEnter)
    this.triggerElement?.removeEventListener("mouseleave", this.handleLeave)
    this.triggerElement?.removeEventListener("focus", this.handleFocus)
    this.triggerElement?.removeEventListener("blur", this.handleBlur)
    document.removeEventListener("keydown", this.handleEscape)
    this.hide()
  }

  show() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }

    if (this.tooltipElement) return
    const html = this.hasContentTarget ? this.contentTarget.innerHTML : ""
    if (!html) return

    this.tooltipElement = document.createElement("div")
    this.tooltipElement.className = "fixed z-[9999]"
    if (this.allowHtmlValue) {
      this.tooltipElement.innerHTML = html
    } else {
      this.tooltipElement.textContent = this.contentTarget?.textContent || ""
    }

    document.body.appendChild(this.tooltipElement)
    this.positionTooltip()

    if (this.interactiveValue) {
      this.tooltipElement.addEventListener("mouseenter", this.handleEnter)
      this.tooltipElement.addEventListener("mouseleave", this.handleLeave)
    }
  }

  hide(event) {
    if (!this.tooltipElement) return

    if (this.interactiveValue && event?.relatedTarget) {
      const movedToTooltip = this.tooltipElement.contains(event.relatedTarget)
      const movedToTrigger = this.triggerElement.contains?.(event.relatedTarget)
      if (movedToTooltip || movedToTrigger) return
    }

    this.hideTimeout = setTimeout(() => {
      if (!this.tooltipElement) return
      if (this.interactiveValue) {
        this.tooltipElement.removeEventListener("mouseenter", this.handleEnter)
        this.tooltipElement.removeEventListener("mouseleave", this.handleLeave)
      }
      this.tooltipElement.remove()
      this.tooltipElement = null
      this.hideTimeout = null
    }, 120)
  }

  positionTooltip() {
    if (!this.tooltipElement) return
    const rect = this.triggerElement.getBoundingClientRect()
    const tooltipRect = this.tooltipElement.getBoundingClientRect()
    const gap = 8

    let top = rect.top + (rect.height - tooltipRect.height) / 2
    let left = rect.right + gap

    if (this.placementValue === "left") {
      left = rect.left - tooltipRect.width - gap
    } else if (this.placementValue === "top") {
      top = rect.top - tooltipRect.height - gap
      left = rect.left + (rect.width - tooltipRect.width) / 2
    } else if (this.placementValue === "bottom") {
      top = rect.bottom + gap
      left = rect.left + (rect.width - tooltipRect.width) / 2
    }

    const minTop = 8
    const maxTop = window.innerHeight - tooltipRect.height - 8
    const minLeft = 8
    const maxLeft = window.innerWidth - tooltipRect.width - 8

    const clampedTop = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop))
    const clampedLeft = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft))

    this.tooltipElement.style.top = `${clampedTop}px`
    this.tooltipElement.style.left = `${clampedLeft}px`
  }

  onEscape(event) {
    if (event.key === "Escape") this.hide()
  }
}
